import React from "react";
import { FiAlignJustify, FiSearch, FiX } from "react-icons/fi";
import { Link } from "react-router-dom";
import { RiMoonFill, RiSunFill } from "react-icons/ri";
import { MdCheck, MdContentCopy, MdExpandMore, MdGroups, MdMenuBook, MdOpenInNew, MdAccountBalanceWallet, MdSettings } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import RoleSwitcher from "components/role/RoleSwitcher";
import HeaderNotifications from "components/navbar/HeaderNotifications";
import { DropdownMenu, MenuItem, MenuLinkItem, MenuSeparator } from "components/ui/DropdownMenu";
import ThemeSelector from "components/ui/ThemeSelector";
import Tooltip from "components/ui/Tooltip";
import { toggleTheme, useTheme } from "lib/theme";
import { ADMIN_PORTAL_URL, roleTheme, seatFromPortal, seatOrganisation } from "lib/seats";
import routes, { routeAllowed } from "routes.js";
import wasaaLogo from "assets/wasaa/wasaa-logo-horizontal.svg";
import wasaaLogoDark from "assets/wasaa/wasaa-logo-horizontal-on-dark.svg";

const ICON_BUTTON =
  "inline-flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-sm text-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 data-[state=open]:bg-accent";

/** Kuest UserInfoSection: 48px avatar, name text-base/tight semibold, a muted line with a copy button. */
function UserInfoSection({ email, subtitle, initial }) {
  const [copied, setCopied] = React.useState(false);
  async function copy(event) {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard blocked: nothing to do
    }
  }
  return (
    <div className="flex items-center gap-4 p-4">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-lg font-semibold text-primary-foreground ring-2 ring-border/20">
        {initial}
      </div>
      <div className="min-w-0 flex-1 space-y-1">
        <p className="truncate text-base font-semibold leading-tight text-foreground" title={email}>
          {email}
        </p>
        <div className="flex min-w-0 items-center gap-1">
          <span className="truncate text-xs text-muted-foreground" title={subtitle}>
            {subtitle}
          </span>
          {email && (
            <button
              type="button"
              onClick={copy}
              title={copied ? "Copied" : "Copy email"}
              aria-label={copied ? "Copied" : "Copy email"}
              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              {copied ? <MdCheck className="h-3.5 w-3.5 text-yes" /> : <MdContentCopy className="h-3.5 w-3.5" />}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function allowed(name, isPartner) {
  const route = routes.find((r) => r.name === name && r.layout === "/portal");
  return Boolean(route && routeAllowed(route, isPartner));
}

/** Kuest admin header: sticky bg-background, logo + section label, search, actions,
 *  and the HeaderDropdownUserMenuAuth-style account menu. */
const Navbar = (props) => {
  const { onOpenSidenav, logoText } = props;
  const { theme } = useTheme();
  const { user, portal, isPartner, platformAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = React.useState("");
  const searchRef = React.useRef(null);

  React.useEffect(() => {
    function onKey(event) {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;
      const tag = (event.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || event.target?.isContentEditable) return;
      if (!searchRef.current || searchRef.current.offsetParent === null) return;
      event.preventDefault();
      searchRef.current.focus();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  function handleLogout() {
    if (!window.confirm("Log out of the portal?")) return;
    logout();
    navigate("/auth/sign-in", { replace: true });
  }

  const initial = user && user.email ? user.email[0].toUpperCase() : "W";
  const seat = seatFromPortal(portal);
  const subtitle = seat ? `${seat.roleLabel} · ${seatOrganisation(seat)}` : logoText;
  const accent = seat ? roleTheme(seat.kind) : null;

  return (
    <header className="sticky top-0 z-40 bg-background">
      <nav className="mx-auto flex min-h-[60px] w-full max-w-[1332px] items-center gap-2 px-4 py-3 sm:gap-4 md:min-h-[68px]">
        <button type="button" aria-label="Open menu" className={`${ICON_BUTTON} -ml-2 xl:hidden`} onClick={onOpenSidenav}>
          <FiAlignJustify className="h-5 w-5" />
        </button>

        <Link to="/portal" className="flex h-10 shrink-0 items-center gap-3 transition-opacity hover:opacity-80">
          <img src={wasaaLogo} alt="Wasaa Markets" className="h-7 w-auto dark:hidden" />
          <img src={wasaaLogoDark} alt="Wasaa Markets" className="hidden h-7 w-auto dark:block" />
          <span className="hidden h-6 w-px bg-border sm:block" aria-hidden="true" />
          <span className="hidden whitespace-nowrap text-lg font-medium text-foreground sm:block">Partner Portal</span>
        </Link>

        {/* Kuest HeaderSearch field: search icon at left-4, `/` kbd hint, clear button. */}
        <div className="relative mx-2 hidden min-w-0 max-w-[600px] flex-1 lg:block">
          <FiSearch className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <input
            ref={searchRef}
            type="text"
            placeholder="Search..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setQuery("");
                e.currentTarget.blur();
              }
            }}
            className="block h-10 w-full rounded-md border border-transparent bg-input pl-11 pr-12 text-sm text-foreground outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus:border-ring focus:ring-[3px] focus:ring-ring/50"
          />
          {query ? (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => {
                setQuery("");
                searchRef.current?.focus();
              }}
              className="absolute right-3 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-sm text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <FiX className="h-4 w-4" />
            </button>
          ) : (
            <kbd className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded-sm border border-border bg-muted px-1.5 text-xs font-medium text-muted-foreground">
              /
            </kbd>
          )}
        </div>

        <div className="ml-auto flex min-w-0 items-center gap-1 sm:gap-2">
          <div className="hidden min-w-0 max-w-[360px] md:block">
            {portal ? (
              <RoleSwitcher variant="navbar" />
            ) : (
              <div className="truncate" title={logoText}>
                <span className="text-sm text-muted-foreground">{logoText}</span>
              </div>
            )}
          </div>

          <HeaderNotifications isPartner={isPartner} triggerClassName={ICON_BUTTON} />

          <Tooltip content={theme === "dark" ? "Light mode" : "Dark mode"} align="end">
            <button type="button" aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"} className={ICON_BUTTON} onClick={toggleTheme}>
              {theme === "dark" ? <RiSunFill className="h-4 w-4" /> : <RiMoonFill className="h-4 w-4" />}
            </button>
          </Tooltip>

          <span className="hidden h-5 w-px bg-border sm:block" aria-hidden="true" />

          {/* Kuest HeaderDropdownUserMenuAuth */}
          <DropdownMenu
            align="end"
            sideOffset={0}
            hoverOpen
            label="User menu"
            className="w-64 font-medium"
            trigger={({ open, props }) => (
              <button
                type="button"
                {...props}
                aria-label="User menu"
                className="group flex h-9 cursor-pointer items-center gap-2 rounded-sm px-2 text-foreground transition-colors hover:bg-accent/70 data-[state=open]:bg-accent/70"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
                  {initial}
                </span>
                <MdExpandMore className={`h-4 w-4 transition-transform duration-150 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
              </button>
            )}
          >
            <UserInfoSection email={user ? user.email : "Not signed in"} subtitle={subtitle} initial={initial} />
            <MenuSeparator />

            <MenuLinkItem to="/portal/profile" className="py-2 font-semibold">
              <MdSettings className="h-4 w-4 text-orange-500" /> Profile &amp; security
            </MenuLinkItem>
            {allowed("Users", isPartner) && (
              <MenuLinkItem to="/portal/users" className="py-2 font-semibold">
                <MdGroups className="h-4 w-4 text-sky-500" /> Users
              </MenuLinkItem>
            )}
            {allowed("Team", isPartner) && (
              <MenuLinkItem to="/portal/team" className="py-2 font-semibold">
                <MdGroups className="h-4 w-4 text-sky-500" /> Team
              </MenuLinkItem>
            )}
            {allowed("Payout Account", isPartner) && (
              <MenuLinkItem to="/portal/payout-account" className="py-2 font-semibold">
                <MdAccountBalanceWallet className="h-4 w-4 text-amber-500" /> Payout account
              </MenuLinkItem>
            )}
            <MenuLinkItem to="/portal/guide" className="py-2 font-semibold">
              <MdMenuBook className="h-4 w-4 text-violet-500" /> Market guide
            </MenuLinkItem>
            {platformAdmin && (
              <MenuLinkItem href={ADMIN_PORTAL_URL} className="py-2 font-semibold">
                <MdOpenInNew className="h-4 w-4 text-current" /> Admin portal
              </MenuLinkItem>
            )}

            <div className="flex items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-sm font-semibold transition-colors hover:bg-accent hover:text-accent-foreground">
              <span>Dark Mode</span>
              <ThemeSelector />
            </div>

            {accent && (
              <div className="md:hidden">
                <MenuSeparator />
                <p className="flex items-center gap-2 px-2 py-1.5 text-xs text-muted-foreground">
                  <span className={`h-2 w-2 shrink-0 rounded-full ${accent.dot}`} aria-hidden="true" />
                  <span className="truncate">{subtitle}</span>
                </p>
              </div>
            )}

            <MenuSeparator />
            <MenuLinkItem href="https://wasaa.markets" target="_blank" rel="noreferrer" className="py-2 font-semibold text-muted-foreground">
              Website
            </MenuLinkItem>
            <MenuLinkItem href="mailto:support@wasaa.markets" className="py-2 font-semibold text-muted-foreground">
              Support
            </MenuLinkItem>
            <MenuItem variant="destructive" onSelect={handleLogout} className="py-2 font-semibold">
              Log Out
            </MenuItem>
          </DropdownMenu>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
